import CarouselCard from '@client/components/shared/VendorProfilesCarousel/CarouselCard/CarouselCard';
import { VendorProfilesCarousel } from '@client/components/shared/VendorProfilesCarousel/VendorProfilesCarousel';
import BringYourListCard from '@components/BringYourListCard/BringYourListCard';
import { isBylNonAuthEntrypointsVariant1 } from '@redux/experiments/selectors/byl-non-auth-entrypoints';
import { bylNonAuthEntrypointsSelector } from '@redux/experiments/selectors/byl-non-auth-entrypoints';
import { experiments } from '@settings';
import { generateSeed } from '@utils/generateSeed';
import { Logger } from '@utils/logger/logger';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import FeaturedSectionLayout from '../../../components/shared/FeaturedSectionLayout';
import TrackableContainer from '../../../components/shared/TrackableContainer';
import ViewAllButton from '../../../components/shared/ViewAllButton';
import { AnalyticsContext } from '../../../contexts/AnalyticsContext/AnalyticsContext';
import { getMemberId } from '../../../redux';
import { getCategory } from '../../../redux/category';
import * as ExperimentActions from '../../../redux/experiments';
import { getCity, getIpAddress, getStateCode } from '../../../redux/location';
import { getAnonymousId } from '../../../redux/settings';
import {
	fetchVendors,
	getVendorProfilesForRequest,
	getVendorTotal,
} from '../../../redux/vendors';
import { renderViewAllText } from '../../../utils/text';
import {
	BANDED_DISTANCE,
	CATEGORY_LANDING_PAGE,
	CLICK_THROUGH_TO_VENDORS,
	CLICK_THROUGH_TO_VENDOR_CATEGORY,
	RECOMMENDED_VENDORS,
	widgetViewed,
} from '../../../utils/tracking';
import urlSafe, {
	INTERNAL_REFERRER_SORT_PARAM,
} from '../../../utils/urlHelpers';
import ViewAllButtonStyles from '../../State/AdditionalCitiesRecommendations/CityRecommendations/styles.scss';
import { ChangeLocationModal } from './ChangeLocationModal';
import Header from './Header';
import Styles from './styles.scss';

export const MIN_VISIBLE_VENDOR_PROFILES = 4;
export const MAX_VENDOR_PROFILES = 8;

/** @internal */
export class Recommendations extends Component {
	static propTypes = {
		category: PropTypes.shape({
			code: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
			plural: PropTypes.shape({
				slug: PropTypes.string.isRequired,
			}).isRequired,
		}).isRequired,
		categoryGuid: PropTypes.string.isRequired,
		city: PropTypes.string.isRequired,
		fetchVendors: PropTypes.func.isRequired,
		ip: PropTypes.string.isRequired,
		memberId: PropTypes.string.isRequired,
		anonymousId: PropTypes.string,
		stateCode: PropTypes.string.isRequired,
		vendorProfiles: PropTypes.instanceOf(Array).isRequired,
		vendorTotal: PropTypes.number.isRequired,
		reportServerSide: PropTypes.func.isRequired,
		bylNonAuthEntrypointsAssignment: PropTypes.oneOf([
			'control',
			'variant1',
			null,
		]),
		isBylNonAuthEntrypointsVariant1: PropTypes.bool,
	};

	state = {
		locationModalIsOpen: false,
	};

	componentDidMount() {
		// load vendors only if none (i.e. client-side routed or not server-side rendered)
		if (this.props.vendorProfiles.length === 0) this.makeFetch();

		const { reportServerSide, bylNonAuthEntrypointsAssignment } = this.props;

		if (bylNonAuthEntrypointsAssignment) {
			Logger.log({
				log_message: `BYL Non-Auth Entrypoints Assignment: ${bylNonAuthEntrypointsAssignment}`,
			});
			reportServerSide(experiments.bylNonAuthEntrypoints);
		}
	}

	componentDidUpdate(prevProps) {
		const { categoryGuid, city, stateCode } = this.props;
		if (
			prevProps.categoryGuid !== categoryGuid ||
			prevProps.city !== city ||
			prevProps.stateCode !== stateCode
		) {
			return this.makeFetch();
		}
		return null;
	}

	makeFetch() {
		const { categoryGuid, city, stateCode, memberId, anonymousId } = this.props;
		return this.props.fetchVendors({
			categoryGuid,
			limit: MAX_VENDOR_PROFILES,
			city,
			state: stateCode,
			member: { userId: memberId, anonymousId },
			seed: generateSeed(),
		});
	}

	toggleModal = () => {
		const locationModalIsOpen = !this.state.locationModalIsOpen;

		return this.setState({ locationModalIsOpen });
	};

	sourceInfo = {
		sourceContent: RECOMMENDED_VENDORS,
		sourceFormula: BANDED_DISTANCE,
		sourcePage: CATEGORY_LANDING_PAGE,
	};

	trackCardClick = () => {
		this.context.track({
			event: CLICK_THROUGH_TO_VENDORS,
			properties: {
				...this.sourceInfo,
				vendorCategoryCode: this.props.category.code,
			},
		});
	};

	trackViewAllClick = () =>
		this.context.track({
			event: CLICK_THROUGH_TO_VENDOR_CATEGORY,
			properties: {
				sourceContent: RECOMMENDED_VENDORS,
				sourcePage: CATEGORY_LANDING_PAGE,
			},
		});

	render() {
		if (this.props.vendorProfiles.length < MIN_VISIBLE_VENDOR_PROFILES) {
			return null;
		}

		const {
			category: {
				code,
				name,
				plural: { slug },
			},
			city,
			ip,
			memberId,
			stateCode,
			vendorTotal,
			vendorProfiles,
			isBylNonAuthEntrypointsVariant1,
		} = this.props;

		// ensure 8 profiles max
		const slicedProfiles = vendorProfiles.slice(0, MAX_VENDOR_PROFILES);

		return (
			<TrackableContainer
				trackingData={widgetViewed(CATEGORY_LANDING_PAGE, code)}
			>
				<div id="recommendations" className={Styles.container}>
					{this.state.locationModalIsOpen && (
						<ChangeLocationModal
							isModalOpen={this.state.locationModalIsOpen}
							closeCallback={() =>
								this.setState({ locationModalIsOpen: false })
							}
						/>
					)}

					{isBylNonAuthEntrypointsVariant1 && (
						<BringYourListCard
							className={Styles.bringYourListCard}
							sourceRedirect="category landing page"
						/>
					)}

					<FeaturedSectionLayout
						header={
							<Header
								name={name}
								city={city}
								stateCode={stateCode}
								onLocationPress={this.toggleModal}
							/>
						}
						cta={
							<ViewAllButton
								className={ViewAllButtonStyles.viewAllButtonWide}
								onClick={this.trackViewAllClick}
								slug={`/marketplace/${urlSafe(
									`${slug}-${city}-${stateCode}`,
								)}?${INTERNAL_REFERRER_SORT_PARAM}`}
								text={renderViewAllText(vendorTotal, true)}
							/>
						}
					>
						<VendorProfilesCarousel label="Vendor profiles carousel" grid>
							{slicedProfiles.map((vendor, i) => (
								<CarouselCard
									key={`carousel-card-${vendor.id}`}
									index={i}
									categoryCode={code}
									impressionType={'Landings: Category'}
									ipAddress={ip}
									memberId={memberId}
									sourceInfo={this.sourceInfo}
									trackCardClick={this.trackCardClick} // segment
									vendorRaw={vendor}
								/>
							))}
						</VendorProfilesCarousel>
					</FeaturedSectionLayout>
				</div>
			</TrackableContainer>
		);
	}
}

/** @internal */
export function mapStateToProps(state) {
	return {
		category: getCategory(state),
		city: getCity(state),
		ip: getIpAddress(state),
		memberId: getMemberId(state),
		anonymousId: getAnonymousId(state),
		stateCode: getStateCode(state),
		vendorProfiles: getVendorProfilesForRequest(
			state,
			state.category.id,
			getCity(state),
			getStateCode(state),
		),
		vendorTotal: getVendorTotal(state),
		bylNonAuthEntrypointsAssignment: bylNonAuthEntrypointsSelector(state),
		isBylNonAuthEntrypointsVariant1: isBylNonAuthEntrypointsVariant1(state),
	};
}

/** @internal */
export const mapDispatchToProps = {
	fetchVendors,
	reportServerSide: ExperimentActions.reportServerSideExperiment,
};

const ConnectedRecommendations = compose(
	connect(mapStateToProps, mapDispatchToProps),
)(Recommendations);

Recommendations.contextType = AnalyticsContext;

export default ConnectedRecommendations;
